import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Header from "../components/Header";
import Footer from "../components/Footer";
import { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import config from '../config/config.json';
import PayPalCheckoutButton from '../components/PayPalCheckoutButton';
import OrderEmailTemplate from '../components/OrderEmailTemplate';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import { message } from 'react-message-popup';

// Interfaces for the data we expect from the API
interface Order {
    id: number;
    date: string;
    shipping: string;
    payment: string;
    status: string;
    clientName: string;
    clientPhone: string;
    clientEmail: string;
    shippingAddressStreet: string;
    shippingAddressCompl: string;
    shippingCity: string;
    shippingState: string;
    shippingZipCode: string;
    shippingPrice: number;
    shippingETA: string;
    country: string;
    currency: string;
    orderSent: string;
    payment_id?: string; // The transaction ID from the payment gateway
}

interface OrderDetailItem {
    price: number;
    cant: number;
    name: string;
    photoMain: string;
}

function OrderDetail() {
    const { t, i18n } = useTranslation("global");
    const { runtimeValues } = useContext(RuntimeValuesContext);
    const [searchParams] = useSearchParams();
    const orderId = searchParams.get('orderId');

    const [order, setOrder] = useState<Order | null>(null);
    const [orderItems, setOrderItems] = useState<OrderDetailItem[]>([]);
    const [emailSent, setEmailSent] = useState(false);
    const [title, setTitle] = useState<string>(`${t("pageTitle.order")} - wicaglobalgear.com`);

    const formatter = useCurrencyFormatter(order?.currency || runtimeValues.currency);    

    useEffect(() => {
        if (!orderId) return;

        const controller = new AbortController();
        const { signal } = controller;

        const fetchOrderData = async () => {
            try {
                const headerPromise = axios.get(`${config.backend.url}/api/orders/header`, { params: { orderId }, signal });
                const itemsPromise = axios.get(`${config.backend.url}/api/orders/items`, { params: { orderId }, signal });
                
                const [headerRes, itemsRes] = await Promise.all([headerPromise, itemsPromise]);
                
                // The backend response for /api/orders/header is missing the 'id'.
                // As a robust workaround, we'll add it here from the URL parameter.
                // The long-term fix should be to include the 'id' in the API response itself.
                const orderDataWithId = {
                    ...headerRes.data,
                    id: parseInt(orderId!, 10), // We know orderId is not null here due to the check above
                };
                setOrder(orderDataWithId);
                setOrderItems(itemsRes.data);

            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log('Request canceled:', error.message);
                } else {
                    console.error('Error fetching order data:', error);
                }
            }
        };

        fetchOrderData();

        // This cleanup function will be called if the component unmounts before the fetch is complete.
        return () => {
            controller.abort();
        };
    }, [orderId]);

    useEffect(() => {
        if (order) {
            setTitle(`${t("pageTitle.order")} #${orderId} - wicaglobalgear.com`);
        }
    }, [order, orderId, t]);

    // Add this useEffect for debugging
    useEffect(() => {
        if (order) {
            console.log("🕵️‍♂️ [DEBUG] Fetched order data:", order);
        }
    }, [order]);

    const sendPaymentConfirmationEmail = useCallback(async (transactionId: string) => {
        if (!order) {
            console.error("Cannot send email, order data is not available.");
            return;
        }

        // Create a temporary order object with the new 'completed' status for the email template.
        const updatedOrderForEmail = {
            ...order,
            status: 'completed',
            payment_id: transactionId,
        };

        try {
            const emailHtml = ReactDOMServer.renderToString(
                <OrderEmailTemplate
                    orderId={order.id}
                    order={updatedOrderForEmail}
                    orderItems={orderItems}
                    formatter={formatter}
                    t={t}
                />
            );
            await axios.post(`${config.backend.url}/api/orders/email`, {
                orderId: orderId,
                emailTo: order.clientEmail,
                emailFrom: "info@wicaglobalgear.com",
                emailSubject: `Payment Confirmation for WICA Global Gear Order #${order.id}`,
                emailContent: emailHtml
            });
            console.log("✅ Payment confirmation email queued for sending.");
        } catch (error) {
            console.error('Error sending payment confirmation email:', error);
        }
    }, [order, orderItems, formatter, t, orderId]);

    const handlePaymentSuccess = useCallback(async (details: { internalOrderId: string; transactionId: string }) => {
// On this page, we just need to reload to see the new 'completed' status.
        await sendPaymentConfirmationEmail(details.transactionId);
        message.success("Payment successful! Refreshing order status...");
        setTimeout(() => window.location.reload(), 1500); // Give user time to see message
    }, [sendPaymentConfirmationEmail]);

    const sendEmail = useCallback(async () => {
        if (!order) return;

        const emailHtml = ReactDOMServer.renderToString(
            <OrderEmailTemplate 
                orderId={orderId ? parseInt(orderId) : 0}
                order={order}
                orderItems={orderItems}
                formatter={formatter}
                t={t}
            />
        );

        try {
            await axios.post(`${config.backend.url}/api/orders/email`, {
                orderId: orderId,
                emailTo: order.clientEmail,
                emailFrom: "info@wicaglobalgear.com",
                emailSubject: `Your WICA Global Gear Order #${orderId} Confirmation`,
                emailContent: emailHtml
            });
            console.log("Confirmation email queued for sending.");
        } catch (error) {
            console.error('Error sending email:', error);
        }
    }, [order, orderItems, orderId, formatter, t]);

    useEffect(() => {
        // Send email only once when the necessary data is available
        if (order && !emailSent && order.orderSent === '') {
            sendEmail();
            setEmailSent(true);
        }
    }, [order, emailSent, sendEmail]);

    const getStatusLabel = (status: string) => {
        // A simple mapping from status keys to translation keys
        const statusMap: { [key: string]: string } = {
            'awaiting_payment': 'orderStatus.awaitingPayment',
            'pending': 'orderStatus.pending',
            'completed': 'orderStatus.completed',
            'shipped': 'orderStatus.shipped',
            'cancelled': 'orderStatus.cancelled',
        };
        // Fallback to the original status if no mapping is found
        return t(statusMap[status] || status);
    };

    const { cartSubtotal, cartTotal } = useMemo(() => {
        const subtotal = orderItems.reduce((acc, item) => acc + ((item.price ?? 0) * (item.cant ?? 0)), 0);
        return { cartSubtotal: subtotal, cartTotal: subtotal + (order?.shippingPrice || 0) };
    }, [orderItems, order?.shippingPrice]);

    if (!order) {
        return (
             <div className="app-container">
                <Header />
                <div className="app-content" style={{justifyContent: 'center', alignItems: 'center'}}><p>{t("orderDetail.loading")}</p></div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <Helmet><title>{title}</title></Helmet>
            <div className="app-container">
                <Header />
                <div className="app-content">
                    <section className="sectionPageContent" id="pageContent">
                        <h1>{t("pageTitle.order")} #{orderId}</h1>
                        <div className="cartContainer">
                            <div className="cartItemContainerLeft">
                                {orderItems.map((item, index) => (
                                    <div className="cartItem" key={index}>
                                        <div className="cartItemImageContainer">
                                            <img src={item.photoMain ? `images/${item.photoMain.trim()}` : ''} alt={item.name} className='cartItemImage' />
                                        </div>
                                        <div className="cartItemName"><span>{item.name}</span></div>
                                        <div className="cartItemCant"><span>{item.cant}</span></div>
                                        <div className="cartItemPrice"><span>{formatter.format(item.price * item.cant)}</span></div>
                                    </div>
                                ))}
                            </div>
                            <div className="cartItemContainerRight">
                                <h3>{t("orderDetail.summary")}</h3>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.date")}:</span></div><div className="cartTotalItemValue"><span>{format(new Date(order.date), 'MMM d, yyyy')}</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.status")}:</span></div><div className="cartTotalItemValue"><span style={{fontWeight: 'bold'}}>{getStatusLabel(order.status)}</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.client")}:</span></div><div className="cartTotalItemValue"><span>{order.clientName}</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.contact")}:</span></div><div className="cartTotalItemValue"><span>{order.clientEmail} / {order.clientPhone}</span></div></div>
                                
                                <h4 style={{marginTop: '20px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>{t("orderDetail.shipping")}</h4>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.method")}:</span></div><div className="cartTotalItemValue"><span>{order.shipping === 'pickup' ? t("orderDetail.pickupLocation") : t("orderDetail.shipping")}</span></div></div>
                                {order.shipping === 'shipping' && (
                                    <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.address")}:</span></div><div className="cartTotalItemValue" style={{textAlign: 'right'}}><span>{`${order.shippingAddressStreet} ${order.shippingAddressCompl}, ${order.shippingCity}, ${order.shippingState} ${order.shippingZipCode}, ${order.country}`}</span></div></div>
                                )}
                                
                                <h4 style={{marginTop: '20px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>{t("orderDetail.paymentDetails")}</h4>
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span>{t("orderDetail.paymentMethod")}:</span></div>
                                    <div className="cartTotalItemValue"><span>{order.payment === 'toBeConvened' ? t("orderDetail.paymentToBeConvened") : order.payment}</span></div>
                                </div>
                                {order.status === 'completed' && order.payment_id && (
                                    <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.transactionId")}:</span></div><div className="cartTotalItemValue" style={{fontSize: '12px', wordBreak: 'break-all'}}><span>{order.payment_id}</span></div></div>
                                )}

                                <h4 style={{marginTop: '20px', borderBottom: '1px solid #eee', paddingBottom: '5px'}}>{t("orderDetail.totals")}</h4>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.subtotal")}:</span></div><div className="cartTotalItemValue"><span>{formatter.format(cartSubtotal)}</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><span>{t("orderDetail.shippingCost")}:</span></div><div className="cartTotalItemValue"><span>{formatter.format(order.shippingPrice)}</span></div></div>
                                <div className="cartItemContainerRightItems" style={{borderTop: '1px solid #ccc', paddingTop: '10px', marginTop: '10px'}}>
                                    <div className="cartTotalItemTitle"><span style={{fontWeight: 'bold'}}>{t("orderDetail.total")}:</span></div>
                                    <div className="cartTotalItemValue"><span style={{fontWeight: 'bold'}}>{formatter.format(cartTotal)}</span></div>
                                </div>

                                {/* Conditional Payment Section */}
                                {/* We add a check for order.id to ensure it exists before rendering the button */}
                                {(order.status === 'awaiting_payment' || order.status === 'pending') && order.id && (
                                    <div style={{marginTop: '30px', padding: '20px', border: '1px solid #e0b400', borderRadius: '8px', backgroundColor: '#fffbeb'}}>
                                        <h3>{t("orderDetail.completePaymentTitle")}</h3>
                                        <p>{t("orderDetail.completePaymentMessage")}</p>
                                        {cartTotal > 0 ? (
                                            <PayPalCheckoutButton
                                                internalOrderId={order.id.toString()}
                                                currency={order.currency}
                                                amount={cartTotal}
                                                onPaymentSuccess={handlePaymentSuccess}
                                            />
                                        ) : (
                                            <p style={{ fontStyle: 'italic', color: '#888' }}>
                                                {t("orderDetail.calculatingTotal")}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {order.status === 'completed' && (
                                    <div style={{marginTop: '30px', padding: '20px', border: '1px solid green', borderRadius: '8px', backgroundColor: '#f0fff0'}}>
                                        <h3>{t("orderDetail.paymentCompleteTitle")}</h3>
                                        <p>{t("orderDetail.paymentCompleteMessage")}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default OrderDetail;